import {
  Box,
  Callout,
  DATE_FORMAT,
  PanelSection,
  Tag,
} from '@dev-spendesk/grapes';
import { parseISO } from 'date-fns';

import { SupplierLogo } from 'common/components/SupplierLogo';
import { useTranslation } from 'common/hooks/useTranslation';
import { CardTransactionDetailsSkeleton } from 'modules/cards/components/CardTransactionDetailsPanel/CardTransactionDetailsSkeleton';
import { useGetCardTransactionDetails } from 'modules/cards/hooks/useGetCardTransactionDetails';
import { type CardProduct } from 'modules/cards/models/cards';
import { getDeclinedReasonTranslationKeys } from 'modules/cards/models/get-declined-reason-translation-keys';
import { formatMoneyWithPrecision2 } from 'modules/subscriptions/models/models';
import { UserAvatar } from 'modules/users/components';
import { logger } from 'src/utils/datadog-log-wrapper';

export type Props = {
  companyId: string;
  transactionId: string;
};

export const CardTransactionDetails = ({ companyId, transactionId }: Props) => {
  const { activeLanguage, localeFormat, t } = useTranslation('global');

  const getCardTransactionDetailsState = useGetCardTransactionDetails({
    companyId,
    transactionId,
  });

  if (getCardTransactionDetailsState.status === 'error') {
    return <Callout title={t('misc.errors.networkError')} variant="alert" />;
  }

  if (getCardTransactionDetailsState.status === 'loading') {
    return <CardTransactionDetailsSkeleton />;
  }

  if (getCardTransactionDetailsState.status !== 'success') {
    return <Callout title={t('misc.errors.networkError')} variant="alert" />;
  }

  const cardTransactionDetails = getCardTransactionDetailsState.data;

  const user = {
    id: cardTransactionDetails.user.id,
    name:
      `${cardTransactionDetails.user.firstName ?? ''} ${cardTransactionDetails.user.lastName ?? ''}`.trim() ||
      null,
    email: cardTransactionDetails.user.email,
    avatar: cardTransactionDetails.user.avatarUrl,
  };

  const getTypeTranslation = (type: CardProduct) => {
    switch (type) {
      case 'single_purchase':
        return t('requests.types.single_purchase');
      case 'subscription':
        return t('requests.types.subscription');
      case 'multi_use':
        return t('requests.types.multi_use_card');
      case 'physical':
        return t('requests.types.plastic_card');
      default: {
        logger.warn(`Unknown card type: ${type}`, {
          team: 'cards-ux',
          scope: 'CardTransactionDetails::getTypeTranslation',
        });
        return '';
      }
    }
  };

  return (
    <>
      <div>
        {cardTransactionDetails.declinedReasonsCodes.map((reason) => {
          const { descriptionKey, explanatoryKey } =
            getDeclinedReasonTranslationKeys(
              reason,
              cardTransactionDetails.card.type,
            );

          return (
            <Callout
              key={reason}
              className="mb-16"
              title={t(descriptionKey)}
              variant="alert"
            >
              {explanatoryKey && t(explanatoryKey)}
            </Callout>
          );
        })}
      </div>
      <PanelSection>
        <div className="flex w-full flex-col gap-8">
          <Box
            variant="secondary"
            className="flex min-h-[80px] flex-col gap-8 py-16"
          >
            <div className="flex min-h-48 items-center gap-40">
              <div className="flex items-center">
                <SupplierLogo
                  name={cardTransactionDetails.supplier?.name ?? undefined}
                  src={cardTransactionDetails.supplier?.url ?? undefined}
                />
                <div className="ml-12">
                  <div className="text-content-primary title-xl">
                    {cardTransactionDetails.supplier?.name ?? t('misc.na')}
                  </div>
                </div>
              </div>
              <div className="ml-auto flex min-w-[100px] items-center justify-end gap-4">
                <div className="flex flex-col gap-4">
                  <span className="flex items-center justify-end gap-8">
                    {cardTransactionDetails.originalCurrencyAmount.currency !==
                      cardTransactionDetails.currencyAmount.currency && (
                      <>
                        <span className="whitespace-nowrap text-content-secondary-bg-secondary line-through title-xl">
                          {formatMoneyWithPrecision2(
                            cardTransactionDetails.originalCurrencyAmount
                              .amount,
                            cardTransactionDetails.originalCurrencyAmount
                              .currency,
                            activeLanguage,
                          )}
                        </span>
                        <span className="text-content-disabled">•</span>
                      </>
                    )}
                    <span className="whitespace-nowrap text-content-secondary-bg-secondary line-through title-xl">
                      {formatMoneyWithPrecision2(
                        cardTransactionDetails.currencyAmount.amount,
                        cardTransactionDetails.currencyAmount.currency,
                        activeLanguage,
                      )}
                    </span>
                  </span>
                  <span className="ml-auto whitespace-nowrap text-content-secondary-bg-secondary body-m">
                    {localeFormat(
                      parseISO(cardTransactionDetails.transactionDateTime),
                      DATE_FORMAT.MEDIUM,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </Box>
          <Box
            variant="secondary"
            className="relative flex min-h-[80px] flex-col justify-center gap-16 py-16"
          >
            <div className="flex items-center">
              <UserAvatar
                size={32}
                id={user.id}
                name={user.name ?? user.email ?? ''}
                avatar={user.avatar ?? undefined}
                className="mr-12"
              />
              <h3 className="min-w-[200px] text-content-primary title-xl">
                {user.name}
              </h3>
              <Tag variant="white" className="ml-auto">
                {getTypeTranslation(cardTransactionDetails.card.type)}
              </Tag>
            </div>
          </Box>
        </div>
        <Tag className="mt-16" variant="alert" iconName="hexagone-cross">
          {t('cards.transactions.failedAt', {
            date: localeFormat(
              parseISO(cardTransactionDetails.transactionDateTime),
              DATE_FORMAT.LONG_WITH_TIME,
            ),
          })}
        </Tag>
      </PanelSection>
      <PanelSection title={t('cards.transactions.description')}>
        <Box
          variant="secondary"
          className="relative flex min-h-[80px] flex-col justify-center gap-16 py-16"
        >
          {cardTransactionDetails.supplierName}
        </Box>
      </PanelSection>
    </>
  );
};
